import React, { useEffect, useRef, useState } from 'react';

import type { HTMLMotionProps } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';

import { Icon } from '@/utils/icons';

interface LazyImageProps extends Omit<HTMLMotionProps<'img'>, 'src' | 'alt' | 'className'> {
  src: string;
  alt: string;
  className?: string;
  placeholderSrc?: string;
  effect?: 'blur' | 'opacity' | 'none';
  threshold?: number;
  priority?: boolean;
  imgClassName?: string;
}

const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className = '',
  placeholderSrc,
  effect = 'blur',
  threshold = 0.1,
  priority = false,
  imgClassName = '',
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [isInView, setIsInView] = useState(
    () => priority || typeof window === 'undefined' || !window.IntersectionObserver
  );
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (priority || !window.IntersectionObserver) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority, threshold]);

  const handleLoad = () => setIsLoaded(true);
  const handleError = () => {
    setError(true);
    setIsLoaded(true);
  };

  return (
    <div ref={imgRef} className={`relative overflow-hidden ${className}`}>
      <AnimatePresence>
        {!isLoaded && !error && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 z-10 bg-neutral-100">
            {placeholderSrc ? (
              <img src={placeholderSrc} alt="" className="h-full w-full object-cover blur-lg" />
            ) : (
              <div className="h-full w-full animate-pulse bg-neutral-200" />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {error ? (
        <div className="flex h-full w-full flex-col items-center justify-center bg-neutral-50 p-4">
          <Icon name="Image" size={32} className="mb-2 text-neutral-300" />
          <span className="text-xs font-bold tracking-widest text-neutral-400 uppercase">
            Not Found
          </span>
        </div>
      ) : (
        isInView && (
          <motion.img
            src={src}
            alt={alt}
            onLoad={handleLoad}
            onError={handleError}
            loading={priority ? 'eager' : 'lazy'}
            initial={effect === 'blur' ? { filter: 'blur(10px)', opacity: 0 } : { opacity: 0 }}
            animate={
              isLoaded
                ? { filter: 'blur(0px)', opacity: 1 }
                : effect === 'blur'
                  ? { filter: 'blur(10px)', opacity: 0 }
                  : { opacity: 0 }
            }
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className={`h-full w-full object-cover transition-transform duration-700 ${imgClassName} ${
              !isLoaded ? 'invisible' : 'visible'
            }`}
            {...props}
          />
        )
      )}
    </div>
  );
};

export default React.memo(LazyImage);
